import { createContext, useContext } from 'react';

export const PREVIEW_PATH = '/preview/ticketing';
/** postMessage sources: the admin page hosting the preview, and the preview frame. */
export const HOST = 'tk-preview-host';
export const FRAME = 'tk-preview-frame';

/** True inside the admin live preview: uploads, OTP emails, bookings and payments are simulated. */
export const PreviewContext = createContext(false);
export const usePreviewMode = () => useContext(PreviewContext);

export const slugify = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '').slice(0, 50)
  .replace(/-+$/, '');

/** The unsaved draft as the server would store it: blank link names fall back to the slugged name. */
export const normalizeConfig = (config) => {
  const groups = (config?.groups || []).map((g, i) => ({ ...g, id: g.id || slugify(g.name) || `group-${i + 1}` }));
  const passes = (config?.passes || []).map((p, i) => ({
    ...p,
    id: p.id || slugify(p.name) || `pass-${i + 1}`,
    price: Number(p.price) || 0,
    benefits: (p.benefits || []).filter((b) => String(b || '').trim()),
  }));
  const clean = (list) => (list || []).filter((item) => String(item || '').trim());
  return {
    ...config,
    groups,
    passes,
    days: clean(config?.days),
    tabs: config?.tabs || [],
    fields: config?.fields || [],
    footerLogos: config?.footerLogos || [],
    instructions: config?.instructions && clean(config.instructions.items).length
      ? { ...config.instructions, items: clean(config.instructions.items) } : null,
  };
};

/** Same shape as GET /api/public/events/{ref}/ticketing, built from the admin's event and draft. */
export const previewView = (event, config) => ({
  eventId: event.id,
  slug: event.slug,
  name: event.name,
  eventDate: event.eventDate,
  eventEndDate: event.eventEndDate,
  location: event.location,
  logoUrl: event.formLogoUrl || event.eventLogo,
  currency: event.currency || 'INR',
  ticketing: normalizeConfig(config),
});

/** Listed passes plus the hidden one a link points at, as the public endpoint does. */
export const visibleTo = (config, passId) => ({ ...config, passes: config.passes.filter((p) => !p.hidden || p.id === passId) });

/** Pages the admin can jump to in the preview. */
export const previewPages = (view) => {
  const ref = view.slug || view.eventId;
  const base = `/register/${ref}`;
  const t = view.ticketing;
  return [
    { path: base, label: 'Landing page' },
    ...t.groups.map((g) => ({ path: `${base}/passes/${g.id}`, label: `Passes · ${g.name || g.id}` })),
    ...t.passes.map((p) => ({ path: `${base}/book/${p.id}`, label: `Form · ${p.name || p.id}${p.hidden ? ' (hidden)' : ''}` })),
  ];
};
